import { create } from "zustand";
import { PodWorkspaceService } from "./podWorkspace.service";
import type { PodWorkspace } from "./podWorkspace.service";
import { toast } from "sonner";
import { PodcastService } from "../podcast/podcast.service";
import { isPodcastPipelineProcessing } from "../../features/podWorkspace/podcastPipelineStatus.util";
import i18n from "../i18n";

interface PodWorkspaceStore {
	// State
	workspaces: PodWorkspace[];
	isLoading: boolean;
	error: string | null;
	isDeletingWorkspace: string | null; // Track which workspace is being deleted
	podcastsByWorkspace: Record<string, any>; // Cache podcasts by workspace ID
	pollingInterval: number | null; // Track the polling interval ID

	// Actions
	fetchWorkspaces: () => Promise<void>;
	fetchWorkspacesWithPodcasts: () => Promise<void>;
	deleteWorkspace: (
		workspaceId: string,
		onSuccess?: () => void,
	) => Promise<void>;
	updateWorkspace: (
		workspaceId: string,
		data: { title?: string },
		onSuccess?: () => void,
	) => Promise<void>;
	createWorkspace: (title: string) => Promise<PodWorkspace | null>;
	startPolling: () => void;
	stopPolling: () => void;
	reset: () => void;
}

export const usePodWorkspaceStore = create<PodWorkspaceStore>((set, get) => ({
	// Initial state
	workspaces: [],
	isLoading: false,
	error: null,
	isDeletingWorkspace: null,
	podcastsByWorkspace: {},
	pollingInterval: null,

	// Fetch all workspaces (simple version without podcasts)
	fetchWorkspaces: async () => {
		if (get().isLoading) return;

		set({ isLoading: true, error: null });
		try {
			const workspaces = await PodWorkspaceService.getAllWorkspacesForUser();
			set({
				workspaces,
				isLoading: false,
			});
		} catch (error) {
			console.error("Failed to fetch workspaces:", error);
			set({
				error:
					error instanceof Error ? error.message : "Failed to fetch workspaces",
				isLoading: false,
			});
		}
	},

	// Fetch workspaces AND their podcasts, with smart polling
	fetchWorkspacesWithPodcasts: async () => {
		const state = get();
		if (state.isLoading) return;

		set({ isLoading: true, error: null });
		try {
			const workspaces = await PodWorkspaceService.getAllWorkspacesForUser();

			// Fetch podcasts for each workspace in parallel
			const podcastMap: Record<string, any> = {};
			await Promise.all(
				workspaces.map(async (ws) => {
					try {
						const podcasts = await PodcastService.getPodcastsByWorkspace(ws.id);
						const firstPodcast =
							podcasts.find((p) => p.status === "COMPLETED") ||
							podcasts.find((p) => p.status === "PROCESSING") ||
							podcasts[0] ||
							null;
						podcastMap[ws.id] = firstPodcast;
					} catch (err) {
						console.error(
							`Failed to fetch podcasts for workspace ${ws.id}:`,
							err,
						);
						podcastMap[ws.id] = null;
					}
				}),
			);

			set({
				workspaces,
				podcastsByWorkspace: podcastMap,
				isLoading: false,
			});

			// Check if any podcasts are processing and manage polling
			const hasProcessingPodcasts = Object.values(podcastMap).some((podcast) =>
				isPodcastPipelineProcessing(podcast),
			);

			if (hasProcessingPodcasts && !state.pollingInterval) {
				// Start polling if we have processing podcasts and not already polling
				get().startPolling();
			} else if (!hasProcessingPodcasts && state.pollingInterval) {
				// Stop polling if no processing podcasts
				get().stopPolling();
			}
		} catch (error) {
			console.error("Failed to fetch workspaces:", error);
			set({
				error:
					error instanceof Error ? error.message : "Failed to fetch workspaces",
				isLoading: false,
			});
		}
	},

	// Delete a workspace
	deleteWorkspace: async (workspaceId: string, onSuccess?: () => void) => {
		set({ isDeletingWorkspace: workspaceId });

		try {
			await PodWorkspaceService.deletePodWorkspace(workspaceId);

			// Remove from local state immediately for optimistic update
			set((state) => ({
				workspaces: state.workspaces.filter((ws) => ws.id !== workspaceId),
				isDeletingWorkspace: null,
			}));

			// Show success toast
			toast.success(i18n.t("podWorkspace.workspaceDeletedSuccess"));

			// Call success callback if provided (for refetching related data)
			if (onSuccess) {
				onSuccess();
			}
		} catch (error) {
			console.error("Failed to delete workspace:", error);
			set({ isDeletingWorkspace: null });

			// Show error toast
			toast.error(i18n.t("podWorkspace.workspaceDeleteError"));

			throw error; // Re-throw so caller can handle if needed
		}
	},

	// Update a workspace
	updateWorkspace: async (
		workspaceId: string,
		data: { title?: string },
		onSuccess?: () => void | Promise<void>,
	) => {
		try {
			const updated = await PodWorkspaceService.updatePodWorkspace(
				workspaceId,
				data,
			);

			// Update local state immediately for optimistic update
			set((state) => ({
				workspaces: state.workspaces.map((ws) =>
					ws.id === workspaceId ? updated : ws,
				),
			}));

			// Show success toast
			toast.success(i18n.t("podWorkspace.workspaceUpdatedSuccess"));

			// Call success callback if provided (for refetching related data)
			if (onSuccess) {
				await onSuccess();
			}
		} catch (error) {
			console.error("Failed to update workspace:", error);

			// Show error toast
			toast.error(i18n.t("podWorkspace.workspaceUpdateError"));

			throw error; // Re-throw so caller can handle if needed
		}
	},

	// Create a new workspace
	createWorkspace: async (title: string) => {
		try {
			const workspace = await PodWorkspaceService.createPodWorkspace({ title });

			// Add to local state
			set((state) => ({
				workspaces: [workspace, ...state.workspaces],
			}));

			toast.success(i18n.t("podWorkspace.workspaceCreatedSuccess"));
			return workspace;
		} catch (error) {
			console.error("Failed to create workspace:", error);
			toast.error(i18n.t("podWorkspace.workspaceCreateError"));
			return null;
		}
	},

	// Start polling for updates every 5 seconds
	startPolling: () => {
		const state = get();
		if (state.pollingInterval) return; // Already polling

		console.log("🔄 Starting automatic workspace polling (every 5 seconds)");
		const intervalId = window.setInterval(() => {
			get().fetchWorkspacesWithPodcasts();
		}, 5000);

		set({ pollingInterval: intervalId as unknown as number });
	},

	// Stop polling
	stopPolling: () => {
		const state = get();
		if (state.pollingInterval) {
			console.log("⏹️ Stopping automatic workspace polling");
			window.clearInterval(state.pollingInterval);
			set({ pollingInterval: null });
		}
	},

	// Reset store
	reset: () => {
		const state = get();
		if (state.pollingInterval) {
			window.clearInterval(state.pollingInterval);
		}
		set({
			workspaces: [],
			isLoading: false,
			error: null,
			isDeletingWorkspace: null,
			podcastsByWorkspace: {},
			pollingInterval: null,
		});
	},
}));

// Helper to get workspace actions
export const getWorkspaceActions = () => {
	const store = usePodWorkspaceStore.getState();
	return {
		fetchWorkspaces: store.fetchWorkspaces,
		fetchWorkspacesWithPodcasts: store.fetchWorkspacesWithPodcasts,
		deleteWorkspace: store.deleteWorkspace,
		updateWorkspace: store.updateWorkspace,
		createWorkspace: store.createWorkspace,
		startPolling: store.startPolling,
		stopPolling: store.stopPolling,
		reset: store.reset,
	};
};

// Export for convenience
export { PodWorkspaceService } from "./podWorkspace.service";
